import { useLoaderData } from "react-router-dom";
import { useCallback, useContext, useEffect, useRef } from 'react';

import { ComponentLoadedContext } from '../../../../AppLoader';
import { LeafletContext } from '../../../../App';

import {
  setActiveSpace,
  spacePositionsSelector,
  useAppDispatch,
  useAppSelector,
  SpaceType,
  setActiveModel,
  activeSpaceSelector,
  showRouteSidebarSelector,
  mapSpacesSelector,
  isEditingSelector,
  currentLayerNodesSelector,
  currentLayerEdgesSelector,
  hasPathsOnCurrentLayerSelector,
  placementIdToSpaceIdSelector,
  preferencesSelector,
  overlayBySpaceIdSelector,
} from '../../../../store';

import './Spaces.scss';
import { SpaceEditing } from './SpaceEditing';
import { Space } from "./Space";
import { SpaceCard } from '../../../Map/SpaceCard';

export function Spaces({
  openModal,
}: {
  openModal: Function;
}) {
  const { spaceId, openSpace } = useLoaderData() as { spaceId: string, openSpace: boolean };
  const { componentLoaded } = useContext(ComponentLoadedContext);
  const { activeMarkerRef, mapRef } = useContext(LeafletContext);
  const dispatch = useAppDispatch();
  const didHandleInitialSpaceFromUrl = useRef(false);

  const activeSpace = useAppSelector(activeSpaceSelector);
  const showRouteSidebar = useAppSelector(showRouteSidebarSelector);

  const isEditing = useAppSelector(isEditingSelector);
  const preferences = useAppSelector(preferencesSelector);
  const spaces = useAppSelector(mapSpacesSelector);
  const spacePositions = useAppSelector(spacePositionsSelector);
  const overlayBySpaceId = useAppSelector(overlayBySpaceIdSelector);

  // Path data for navigation dropdown in viewer mode
  const pathNodes = useAppSelector(currentLayerNodesSelector);
  const pathEdges = useAppSelector(currentLayerEdgesSelector);
  const hasIndoorPaths = useAppSelector(hasPathsOnCurrentLayerSelector);
  const placementIdToSpaceId = useAppSelector(placementIdToSpaceIdSelector);

  const showNavigation = preferences.wayfindingDropdowns
    && hasIndoorPaths
    && !isEditing;

  const resolveSpaceId = useCallback((spaceId: string | undefined): string | undefined => {
    if (!spaceId) return undefined;
    const spaceIdStr = String(spaceId);
    if (spacePositions[spaceIdStr]) return spaceIdStr;
    const mapped = placementIdToSpaceId[spaceIdStr];
    return mapped || spaceIdStr;
  }, [spacePositions, placementIdToSpaceId]);

  const findNodeBySpaceId = useCallback((spaceId: string) => {
    const spaceIdStr = String(spaceId);
    const node = pathNodes.find((n) => n.spaceId && String(n.spaceId) === spaceIdStr);
    if (node) return node;
    const resolvedNode = pathNodes.find((n) => {
      if (!n.spaceId) return false;
      return resolveSpaceId(n.spaceId) === spaceIdStr;
    });
    if (resolvedNode) return resolvedNode;
    const targetPos = spacePositions[spaceIdStr];
    if (targetPos) {
      return pathNodes.find((n) =>
        n.spaceId &&
        Math.abs(n.position.x - targetPos.x) < 0.001 &&
        Math.abs(n.position.y - targetPos.y) < 0.001
      );
    }
    return undefined;
  }, [pathNodes, spacePositions, resolveSpaceId]);

  const getConnectedSpaces = useCallback((spaceId: string): SpaceType[] => {
    const spaceNode = pathNodes.find((n) => n.spaceId === spaceId);
    if (!spaceNode) return [];

    const adjacency: Record<string, string[]> = {};
    pathNodes.forEach((n) => { adjacency[n.id] = []; });
    pathEdges.forEach((e) => {
      if (adjacency[e.from]) adjacency[e.from].push(e.to);
      if (adjacency[e.to]) adjacency[e.to].push(e.from);
    });

    const visited = new Set<string>();
    const queue = [spaceNode.id];
    visited.add(spaceNode.id);
    while (queue.length > 0) {
      const current = queue.shift()!;
      for (const neighbor of (adjacency[current] || [])) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }

    const reachableSpaceIds = Array.from(visited)
      .map((nodeId) => {
        const node = pathNodes.find((n) => n.id === nodeId);
        if (!node?.spaceId) return undefined;
        const resolved = resolveSpaceId(node.spaceId);
        if (resolved && spacePositions[resolved]) return resolved;
        return undefined;
      })
      .filter((id): id is string => !!id && id !== String(spaceId));

    return spaces
      .filter((s) => reachableSpaceIds.includes(String(s.id)))
      .sort ((a, b) => a.name.localeCompare(b.name));
  }, [pathNodes, pathEdges, spaces, spacePositions, resolveSpaceId]);

  const setActive = useCallback((space: SpaceType) => {
    dispatch(setActiveSpace(space.id));
    dispatch(setActiveModel(space.vr_model));
  }, [dispatch]);

  // Close space popup when route sidebar opens
  useEffect(() => {
    if (showRouteSidebar && activeSpace) {
      const marker = activeMarkerRef.current[activeSpace.id];
      if (marker) {
        marker.closePopup();
      }
      dispatch(setActiveSpace(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRouteSidebar, activeSpace, dispatch]);

  // Opens popup/card from URL (spaceId), waiting until spaces/markers are ready.
  useEffect(() => {
    if (!componentLoaded || didHandleInitialSpaceFromUrl.current || !spaceId) return;

    const space = spaces.find((s) => String(s.id) === String(spaceId));
    if (!space) return;

    const position = spacePositions[String(space.id)];
    if (!position || !mapRef.current) return;

    if (preferences.popupStyle === 'card') {
      setActive(space);
      if (openSpace) {
        openModal();
      }
      requestAnimationFrame(() => {
        mapRef.current!.flyTo([position.x, position.y]);
      });
      didHandleInitialSpaceFromUrl.current = true;
      return;
    }

    const marker = activeMarkerRef.current[String(space.id)];
    if (!marker) return;

    if (openSpace) {
      setActive(space);
      openModal();
    }

    requestAnimationFrame(() => {
      if (marker.getPopup()) {
        marker.openPopup();
      } else if (!openSpace) {
        // Spaces shown as overlays open their details instead
        marker.fire('click');
      }
      mapRef.current!.flyTo([position.x, position.y]);
    });
    didHandleInitialSpaceFromUrl.current = true;
  }, [
    componentLoaded,
    spaceId,
    spaces,
    spacePositions,
    preferences.popupStyle,
    openSpace,
    openModal,
    setActive,
    mapRef,
    activeMarkerRef,
  ]);

  if (isEditing) {
    return <SpaceEditing />;
  }


  return (
    <>
      {spaces.map((space) => (
        <Space
          key={space.id}
          space={space}
          position={[spacePositions[space.id].x, spacePositions[space.id].y]}
          hiddenMarker={!!overlayBySpaceId[space.id]}
          openModal={openModal}
          setActive={setActive}
          connectedSpaces={getConnectedSpaces(space.id)}
          findNodeBySpaceId={findNodeBySpaceId}
        />
      ))}
      {activeSpace && preferences.popupStyle === 'card' && (
        <SpaceCard
          onOpenModal={() => openModal()}
          connectedSpaces={getConnectedSpaces(activeSpace.id)}
          showNavigation={showNavigation && !!findNodeBySpaceId(activeSpace.id)}
        />
      )}
    </>
  );
}
